import UnauthenticatedLayout from '@/components/shared/layouts/unauthenticated-layout';
import { Button, Stack, Text, Title } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import Link from 'next/link';

export default function PrivacyPolicy() {
    return (
        <UnauthenticatedLayout title="Privacy Policy">
            <Stack gap="4px" mb="lg">
                <Title>Privacy Policy</Title>
                <Text size="sm">Last updated August 17, 2025</Text>
            </Stack>

            <Stack gap="md">
                {/* Overview */}
                <section>
                    <h2>Overview</h2>
                    <p>
                        This Privacy Policy describes how we at DVMReach, Inc. and its affiliates
                        and subsidiaries (<strong>“DVMReach”</strong>, <strong>“we”</strong>,{' '}
                        <strong>“our”</strong>, or <strong>“us”</strong>) collect, use, share, and
                        safeguard information in connection with our websites, services, and
                        products (collectively, the “Services”).
                    </p>
                    <p>
                        We are committed to protecting your privacy and handling your information in
                        a way that is transparent, secure, and compliant with applicable privacy and
                        data protection laws. This Privacy Policy applies to all users of our
                        Services, including veterinary practices, their staff, and their clients,
                        and covers information we collect both online and offline.
                    </p>
                    <p>
                        By using our Services, you agree to the practices described in the Privacy
                        Policy. If you do not agree, you should not use our Services. We also
                        maintain administrative, technical, and physical safeguards designed to
                        protect your data in accordance with SOC 2 Trust Services Criteria,
                        including measures for security, availability, confidentiality, and privacy.
                    </p>
                    <p>
                        To exercise your data protection rights or for more information about
                        DVMReach’s data protection practices, please contact us at{' '}
                        <strong>security@dvmreach.com</strong>.
                    </p>
                    <p>This Privacy Policy covers the following topics:</p>
                    <ul>
                        <li>Overview</li>
                        <li>Types of Data We Collect</li>
                        <li>When and How DVMReach Collects Data</li>
                        <li>How does DVMReach use Personal Data?</li>
                        <li>Messaging &amp; MFA</li>
                        <li>DVMReach AI Training</li>
                        <li>Automated Decision Making</li>
                        <li>What is “Legal Basis”?</li>
                        <li>Data Subject Rights</li>
                        <li>How We Keep Your Data Secure</li>
                        <li>Personal Data Disclosures</li>
                        <li>Disclosure With Your Consent</li>
                        <li>Disclosure Without Your Consent</li>
                        <li>International Data Transfers</li>
                        <li>Data Retention</li>
                        <li>Cookie Policy, Third Party Analytics, and Tracking</li>
                        <li>Other Vendor-specific Privacy Terms</li>
                        <li>Google Workspace</li>
                        <li>Data Privacy Framework</li>
                        <li>Privacy of Minors</li>
                        <li>Changes and Amendments</li>
                        <li>Where can I ask questions about this policy?</li>
                        <li>Acceptance of this Privacy Policy</li>
                        <li>Region Specific Provisions</li>
                    </ul>
                    <p>
                        Your use of DVMReach’s Services is also subject to the Services Agreement
                        that governs your account (the <strong>“Services Agreement”</strong>). Any
                        terms we use in this Privacy Policy without defining them have the
                        definitions given to them in our{' '}
                        <Link href="/terms-of-service">Terms of Service</Link> or the Applicable
                        Data Protection Laws.
                    </p>
                    <p>
                        <strong>“Applicable Data Protection Laws”</strong> means all laws and
                        regulations applicable to the processing of Personal Data under the Services
                        Agreement, including the CCPA (defined below), as well as any future
                        amending acts of the above-mentioned data protection laws any other
                        applicable international, federal, national and state privacy and data
                        protection laws, rules, and regulations pertaining to privacy, data
                        processing and use, data protection, data security, encryption, or
                        confidentiality.
                    </p>
                </section>

                {/* Types of Data We Collect */}
                <section>
                    <h2>Types of Data We Collect</h2>
                    <p>We collect two broad categories of information:</p>
                    <ul>
                        <li>
                            <strong>Personal Data</strong> – information that identifies, relates
                            to, describes, or could reasonably be linked to an individual, such as:
                            <ul>
                                <li>Name, address, email address, and phone number;</li>
                                <li>Date of birth or age;</li>
                                <li>
                                    Payment card or billing details (processed securely through
                                    third-party payment processors);
                                </li>
                                <li>
                                    Client and patient records (including pet name, breed, species,
                                    medical history, and appointment history);
                                </li>
                                <li>Account login credentials;</li>
                                <li>
                                    Voice recordings, call summaries, and transcriptions generated
                                    by our services; and
                                </li>
                                <li>
                                    Any other information you choose to provide that identifies you
                                    or your clients.
                                </li>
                            </ul>
                        </li>

                        <li>
                            <strong>Non-Personal Data</strong> – information that does not directly
                            identify an individual including:
                            <ul>
                                <li>Aggregated or anonymized usage data;</li>
                                <li>Device type, operating system, and browser type;</li>
                                <li>Internet Protocol (IP) address;</li>
                                <li>Date and time of Service access;</li>
                                <li>Pages or features accessed; and</li>
                                <li>
                                    General location information derived from IP address or device
                                    settings.
                                </li>
                            </ul>
                        </li>
                    </ul>
                    <p>
                        We only collect the information necessary to provide and improve our
                        Services, to comply with legal obligations, and for other purposes described
                        in this Privacy Policy.
                    </p>
                </section>

                {/* When and How DVMReach Collects Data */}
                <section>
                    <h2>When and How DVMReach Collects Data</h2>
                    <p>
                        We collect information from you and about you in the following situations:
                    </p>
                    <h3>a. When you provide it to us directly</h3>
                    <ul>
                        <li>When you create an account or update your profile;</li>
                        <li>
                            When you subscribe to our services, request support, or communicate with
                            us via phone, email, chat, or web form;
                        </li>
                        <li>
                            When you upload, input, or share information through our platform
                            (including client/patient data, voice recordings, call summaries, and
                            transcriptions);
                        </li>
                        <li>
                            When you participate in surveys, promotions, or marketing activities;
                        </li>
                        <li>When you make a payment for our services.</li>
                    </ul>

                    <h3>b. Automatically when you use the Services</h3>
                    <ul>
                        <li>
                            We collect device information, usage data, and activity logs
                            automatically through cookies, pixels, analytics tools, and server logs;
                        </li>
                        <li>
                            We may record call metadata (date, time, duration, participants) when
                            you use our VetEcho or similar communication tools;
                        </li>
                        <li>
                            We track in-app actions and feature usage to help improve the Services
                            and detect security issues.
                        </li>
                    </ul>

                    <h3>c. From third parties</h3>
                    <ul>
                        <li>
                            From integration partners such as Practice Information Management
                            Systems (PIMS) and other authorized APIs;
                        </li>
                        <li>
                            From business partners and service providers assisting in payment
                            processing, marketing, analytics, or customer support;
                        </li>
                        <li>
                            From publicly available sources or databases, where permitted by law.
                        </li>
                    </ul>

                    <p>
                        We collect and process your information where we have a lawful basis to do
                        so, including:
                    </p>
                    <ul>
                        <li>To perform a contract with you (e.g., providing our services);</li>
                        <li>To comply with legal obligations;</li>
                        <li>
                            For our legitimate business interests, such as improving services,
                            securing our systems, and preventing fraud; or
                        </li>
                        <li>With your consent, where required.</li>
                    </ul>

                    <p>
                        All information we collect is handled in accordance with our data protection
                        policies and secured using administrative, technical, and physical controls
                        designed to protect against unauthorized access, disclosure, alteration, or
                        destruction.
                    </p>
                </section>

                {/* How does DVMReach use the Data We Collect */}
                <section>
                    <h2>How does DVMReach use the Data We Collect?</h2>
                    <p>We use the information we collect for the following purposes:</p>
                    <h3>a. To provide the Services</h3>
                    <ul>
                        <li>Set up, manage, and maintain your account;</li>
                        <li>Deliver requested features and functionality;</li>
                        <li>Process transactions and send billing-related communications;</li>
                        <li>
                            Facilitate integrations with third-party tools and systems (such as
                            PIMS).
                        </li>
                    </ul>

                    <h3>b. To improve and customize the Services</h3>
                    <ul>
                        <li>Monitor usage trends and performance;</li>
                        <li>Develop new features and functionality;</li>
                        <li>Personalize your experience and content.</li>
                    </ul>

                    <h3>c. For communication and support</h3>
                    <ul>
                        <li>Respond to your questions, requests, and feedback;</li>
                        <li>
                            Send administrative messages, service updates, and important notices;
                        </li>
                        <li>Provide training, onboarding, and troubleshooting support.</li>
                    </ul>

                    <h3>d. For security, fraud prevention, and compliance</h3>
                    <ul>
                        <li>
                            Detect, prevent, and investigate security breaches, fraud, or other
                            unauthorized activity;
                        </li>
                        <li>Enforce our Terms of Service and other agreements;</li>
                        <li>
                            Comply with applicable laws, regulations, and lawful requests from
                            authorities.
                        </li>
                    </ul>

                    <h3>e. For analytics and business operations</h3>
                    <ul>
                        <li>
                            Aggregate and anonymize data to understand how our Services are used;
                        </li>
                        <li>Conduct research and analysis to improve business processes;</li>
                        <li>
                            Generate benchmarking reports without identifying individuals or
                            specific businesses.
                        </li>
                    </ul>

                    <h3>f. With Your Consent</h3>
                    <ul>
                        <li>
                            For marketing communications, surveys, or promotional campaigns you opt
                            into;
                        </li>
                        <li>
                            For any other purpose disclosed to you at the time of collection and
                            with your approval;
                        </li>
                    </ul>

                    <p>
                        We do not sell or rent your personal data to third parties. We will only use
                        your personal data for the purposes described in this Privacy Policy or as
                        otherwise disclosed to you and agreed upon. We use de-identified or
                        aggregated data to achieve our goals while minimizing the use of personal
                        data.
                    </p>
                </section>

                {/* Messaging & MFA */}
                <section>
                    <h2>Messaging &amp; MFA</h2>
                    <h3>a. Messaging</h3>
                    <p>
                        We may send you messages through email, SMS/text, in-app notifications, or
                        other communication channels, including:
                    </p>
                    <ul>
                        <li>Service announcements and important account notices;</li>
                        <li>Security alerts or incident notifications;</li>
                        <li>Billing-related communications;</li>
                        <li>Feature updates or changes to our terms or policies; and</li>
                        <li>Responses to your requests or support tickets.</li>
                    </ul>

                    <h3>b. Multi-Factor Authentication (MFA)</h3>
                    <p>
                        To help protect your account, we may require or offer multi-factor
                        authentication (MFA). MFA adds an additional layer of security by requiring
                        you to provide a second form of verification (such as a temporary code sent
                        to your device or generated by an authenticator app) in addition to your
                        username and password.
                    </p>
                    <p>
                        You are responsible for maintaining control of your MFA devices or
                        credentials. If you suspect your account credentials or MFA method have been
                        compromised, you must notify us immediately at{' '}
                        <strong>security@dvmreach.com</strong> so we can help secure your account.
                    </p>
                </section>

                {/* Account Access and Credentials */}
                <section>
                    <h2>Account Access and Credentials</h2>
                    <p>
                        You are responsible for maintaining the confidentiality of your account
                        credentials, including your username and password. You must not share your
                        login information or grant access to your account or our systems to any
                        other person or entity without our prior written consent. You are
                        responsible for all activities that occur under your account, whether or not
                        you authorized them, and you must notify us immediately at{' '}
                        <strong>security@dvmreach.com</strong> if you believe your account has been
                        compromised.
                    </p>
                </section>

                {/* DVMReach AI Training */}
                <section>
                    <h2>DVMReach AI Training</h2>
                    <h3>a. Purpose of AI Training</h3>
                    <p>
                        We may use certain data to improve and develop our artificial intelligence
                        (AI) and machine learning (ML) models that power features within our
                        Services. This process helps us enhance accuracy, speed, and reliability
                        over time.
                    </p>

                    <h3>b. Data Handling &amp; Privacy Safeguards</h3>
                    <ul>
                        <li>
                            <strong>Anonymization/De-identification:</strong> Before using any data
                            for AI training, we remove or alter personal identifiers so the
                            information cannot reasonably be linked back to you, your clients, or
                            your patients.
                        </li>
                        <li>
                            <strong>Aggregate Use:</strong> We may combine anonymized data from
                            multiple users to train and evaluate AI models.
                        </li>
                        <li>
                            <strong>Limited Scope:</strong> We only use the minimum amount of data
                            necessary to achieve the intended training purpose.
                        </li>
                        <li>
                            <strong>Access Controls:</strong> AI training data is stored and
                            processed in secure environments with restricted access, consistent with
                            SOC 2 Trust Services Criteria for confidentiality and privacy.
                        </li>
                    </ul>

                    <h3>c. No sale of data for AI purposes</h3>
                    <p>
                        We do not sell your data to third parties for AI or ML purposes. Any use of
                        your data for AI development is solely to improve our own products and
                        services.
                    </p>

                    <h3>d. User choices &amp; transparency</h3>
                    <p>
                        If you have questions about how your data may be used for AI model training,
                        or if you wish to request that your data not be included in future training
                        datasets, you can contact us at <strong>privacy@dvmreach.com</strong>.
                        Please note that this will not affect the use of anonymized data that cannot
                        reasonably be linked back to you.
                    </p>
                </section>

                {/* How does DVMReach Share Data? */}
                <section>
                    <h2>How does DVMReach Share Data?</h2>
                    <h3>a. Service providers &amp; subprocessors</h3>
                    <p>
                        We share personal data with trusted third-party service providers
                        (“subprocessors”) who perform services on our behalf, such as:
                    </p>
                    <ul>
                        <li>Cloud hosting and storage providers;</li>
                        <li>Payment processors;</li>
                        <li>Customer support platforms;</li>
                        <li>Analytics and performance monitoring tools; and</li>
                        <li>Communication and marketing platforms.</li>
                    </ul>
                    <p>
                        These providers are only authorized to use your data as necessary to perform
                        the services we request, and they are contractually obligated to protect
                        your data in accordance with applicable privacy laws and our own security
                        standards.
                    </p>

                    <h3>b. Business partners &amp; integrations</h3>
                    <p>
                        If you connect our Services to third-party systems (such as Practice
                        Information Management Systems or other integrations), we may share the data
                        you authorize with those systems to facilitate the integration. These third
                        parties are responsible for their own privacy and security practices.
                    </p>

                    <h3>c. Legal compliance &amp; safety</h3>
                    <p>
                        We may disclose your data when we believe in good faith that it is necessary
                        to:
                    </p>
                    <ul>
                        <li>
                            Comply with any applicable law, regulation, legal process, or
                            governmental request;
                        </li>
                        <li>Protect the security or integrity of our Services;</li>
                        <li>Enforce our agreements, policies, and terms; or</li>
                        <li>
                            Protect the rights, property, or safety of DVMReach, our users, or
                            others.
                        </li>
                    </ul>

                    <h3>d. Corporate Transactions</h3>
                    <p>
                        If we are involved in a merger, acquisition, financing, reorganization,
                        bankruptcy, or sale of assets, your data may be transferred as part of that
                        transaction, subject to the same commitments set forth in this Privacy
                        Policy.
                    </p>

                    <h3>e. Aggregated &amp; De-identified data</h3>
                    <p>
                        We may share aggregated or de-identified information that cannot reasonably
                        be used to identify you, for purposes such as industry analysis, research,
                        marketing, and improving our services.
                    </p>

                    <h3>f. No sale of personal data</h3>
                    <p>
                        We do not sell or rent your personal data to third parties for marketing
                        purposes.
                    </p>

                    <p>
                        We maintain a current list of subprocessors and service providers, which is
                        available upon request by emailing <strong>privacy@dvmreach.com</strong>.
                        All subprocessors are subject to due diligence, contractual confidentiality
                        requirements, and ongoing security reviews consistent with SOC 2 Trust
                        Services Criteria.
                    </p>
                </section>

                {/* What is “Legal Basis”? */}
                <section>
                    <h2>What is “Legal Basis”?</h2>
                    <p>
                        “Legal basis” refers to the lawful grounds under which we are permitted to
                        collect, use, and share your personal data. Depending on where you live,
                        privacy laws (such as the General Data Protection Regulation (GDPR) in the
                        EU or the UK GDPR) require us to identify at least one legal basis for each
                        type of processing we carry out.
                    </p>
                    <p>The legal bases we rely on include:</p>
                    <ul>
                        <li>
                            <strong>a. Performance of a contract</strong> — We process your data
                            when it is necessary to fulfill our agreement with you — for example, to
                            provide the Services, process payments, or deliver customer support.
                        </li>
                        <li>
                            <strong>b. Compliance with a legal obligation</strong> — We process your
                            data when required to comply with applicable laws or regulations, such
                            as maintaining transaction records for tax or accounting purposes.
                        </li>
                        <li>
                            <strong>c. Legitimate interests</strong> — We process your data when it
                            is necessary for our legitimate business interests, provided that your
                            rights and freedoms do not override those interests. Examples include
                            improving our services, detecting and preventing fraud, and ensuring
                            network and information security.
                        </li>
                        <li>
                            <strong>d. Consent</strong> — We process your data when you have given
                            us explicit permission to do so for a specific purpose, such as
                            receiving marketing communications or participating in surveys. You can
                            withdraw your consent at any time by contacting us at{' '}
                            <strong>privacy@dvmreach.com</strong>.
                        </li>
                    </ul>

                    <p>
                        As part of our SOC 2–aligned privacy program, we document and review our
                        processing activities to ensure that a valid legal basis exists for each,
                        and we maintain records of these decisions in accordance with applicable
                        data protection laws.
                    </p>
                </section>

                {/* Data Subject Rights */}
                <section>
                    <h2>Data Subject Rights</h2>
                    <p>
                        Depending on where you live, you may have certain rights over your personal
                        data. These may include the right to:
                    </p>
                    <ul>
                        <li>Access a copy of the personal data we hold about you;</li>
                        <li>Correct inaccurate or incomplete data;</li>
                        <li>Delete your personal data (“right to be forgotten”);</li>
                        <li>Restrict or object to certain processing activities;</li>
                        <li>Receive your data in a portable format;</li>
                        <li>Withdraw consent where we rely on it; and</li>
                        <li>
                            Opt out of the sale or sharing of personal data (where applicable under
                            laws like CCPA/CPRA).
                        </li>
                    </ul>
                    <p>
                        To exercise these rights, contact us at{' '}
                        <strong>privacy@dvmreach.com</strong>. We may need to verify your identity
                        before responding. We will respond within the timeframe required by
                        applicable law. Please note that some rights may be limited — for example,
                        where fulfilling your request would conflict with our legal obligations or
                        our ability to provide the Services.
                    </p>
                </section>

                {/* How We Keep Your Data Secure */}
                <section>
                    <h2>How We Keep Your Data Secure</h2>
                    <p>
                        We use a combination of administrative, technical, and physical safeguards
                        to protect your personal data from unauthorized access, disclosure,
                        alteration, or destruction.
                    </p>
                    <p>These measures include:</p>
                    <ul>
                        <li>Encryption of data in transit and at rest;</li>
                        <li>
                            Access controls and authentication requirements (including MFA where
                            applicable);
                        </li>
                        <li>Network monitoring and intrusion detection systems;</li>
                        <li>Regular security training for employees; and</li>
                        <li>Vendor security reviews for all third-party subprocessors.</li>
                    </ul>
                    <p>
                        Our security program is designed to align with the SOC 2 Trust Services
                        Criteria for security, availability, confidentiality, and privacy. While no
                        system is completely secure, we continually monitor, test, and improve our
                        safeguards to protect your information.
                    </p>
                </section>

                {/* Data Retention */}
                <section>
                    <h2>Data Retention</h2>
                    <p>
                        We keep your personal data only for as long as necessary to fulfill the
                        purposes described in this Privacy Policy, unless a longer retention period
                        is required or permitted by law.
                    </p>
                    <p>Retention periods vary depending on:</p>
                    <ul>
                        <li>The type of data;</li>
                        <li>The purpose for which we collected and use it; and</li>
                        <li>Legal, regulatory, or contractual requirements.</li>
                    </ul>
                    <p>
                        <strong>Deletion &amp; Anonymization:</strong> When data is no longer
                        needed, we securely delete it or anonymize it so it can no longer be linked
                        to you. We may continue to use aggregated or de-identified data for
                        analytics, service improvement, and research.
                    </p>
                </section>

                {/* Cookie Policy, Third Party Analytics, and Tracking */}
                <section>
                    <h2>Cookie Policy, Third Party Analytics, and Tracking</h2>
                    <p>
                        DVMReach uses third-party service providers to understand how users enter,
                        navigate, and leave the website and Services, the frequency and length of
                        visits to the Services or third-party websites, application or device usage
                        data, and your product or service preference indicated by the number of
                        times and the length of time you view a product. DVMReach and its
                        third-party providers gather this data using cookies, web beacons, tags,
                        video and chat windows, and other similar techniques which allow these
                        networks to provide website capabilities, research, and reporting for us and
                        advertisers.
                    </p>
                    <p>
                        You may be able to opt out of tracking conducted by third parties through
                        our Services by adjusting the Do Not Track settings on your browser, but we
                        don’t control whether or how these third parties comply with Do Not Track
                        requests. Our Services do not support Do Not Track requests at this time,
                        which means that we collect information about your online activity while you
                        are using the Services.
                    </p>
                </section>

                {/* Privacy of Minors */}
                <section>
                    <h2>Privacy of Minors</h2>
                    <p>
                        DVMReach does not provide services designed for use by children under the
                        age of 18, nor does it knowingly collect or solicit Personal Data from
                        anyone under the age of 18. If we learn that we have collected such
                        information we will delete that information as quickly as possible. If you
                        believe that a child under 18 has provided us with Personal Data, please
                        contact us at <strong>support@dvmreach.com</strong>.
                    </p>
                </section>

                {/* Changes and Amendments */}
                <section>
                    <h2>Changes and Amendments</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in
                        our practices, legal requirements, or services. If we make material changes,
                        we will notify you by posting the updated policy on our website and updating
                        the “Last Updated” date. Where required by law, we will also seek your
                        consent to the changes. Your continued use of our Services after the
                        effective date of the updated Privacy Policy means you accept the changes.
                    </p>
                </section>

                {/* Contact & Questions */}
                <section>
                    <h2>Contact &amp; Questions</h2>
                    <p>
                        If you have any questions, concerns, or complaints about this Privacy Policy
                        or our data protection practices, please do not hesitate to contact our Data
                        Protection Officer by emailing us at <strong>support@dvmreach.com</strong>{' '}
                        or by writing to us at the following address:
                    </p>
                    <address>
                        DVMReach, Inc.
                        <br />
                        PO Box 1303
                        <br />
                        American Fork, UT 84003
                    </address>
                </section>

                {/* Acceptance of this Privacy Policy */}
                <section>
                    <h2>Acceptance of this Privacy Policy</h2>
                    <p>
                        By using the Website or its Services, you agree to be bound by this Privacy
                        Policy. If you do not agree to abide by the terms of this Privacy Policy,
                        you are not authorized to use or access the Website and its Services.
                    </p>
                </section>

                {/* Region Specific Provisions */}
                <section>
                    <h2>Region Specific Provisions</h2>
                    <h3>California and US States</h3>
                    <p>
                        Under some U.S. state laws, including the California Consumer Privacy Act of
                        2018 (as amended by the California Consumer Privacy Rights Act) (
                        <strong>“CCPA”</strong>), businesses that collect personal data of residents
                        are required to make certain disclosures regarding how they collect, use and
                        disclose such information, as well as rights available to residents. This
                        section addresses those requirements.
                    </p>
                    <p>
                        <strong>DVMReach does not sell or share your Personal Data.</strong>{' '}
                        DVMReach will only disclose your Personal Data as detailed in this Privacy
                        Policy.
                    </p>
                    <p>
                        Residents have the right to make requests with regard to certain information
                        we collect about them, at no charge, two times every 12 months, including
                        the right to request disclosure and/or a copy of data collected by DVMReach,
                        as well as the right to delete any such data and/or opt-out of the sale or
                        sharing of such data. At all times, you have the right not to be
                        discriminated against when exercising any of these rights. You may exercise
                        those rights by emailing us at <strong>support@dvmreach.com</strong>.
                    </p>
                </section>
            </Stack>

            <Button
                mt="lg"
                mx="auto"
                display="block"
                maw="250px"
                color="black"
                leftSection={<IconHome size="1rem" />}
                component={Link}
                href="/"
            >
                dvmGateway Home
            </Button>
        </UnauthenticatedLayout>
    );
}
